import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, MapPin, Heart, Phone, Search, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { BottomNav } from '../components/BottomNav';

export default function FindHelpPage() {
  const navigate = useNavigate();
  const [zipCode, setZipCode] = useState('');

  const handleFindResources = () => {
    // Opens external FindHelp page with ZIP code
    const url = zipCode 
      ? `https://www.findhelp.org/?postal=${zipCode}`
      : 'https://www.findhelp.org/';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const resourceCategories = [
    { icon: Heart, label: 'Healthcare', description: 'Free clinics, health services' },
    { icon: '🍎', label: 'Food', description: 'Food banks, SNAP assistance' },
    { icon: '🏠', label: 'Housing', description: 'Shelters, rent assistance' },
    { icon: '🚌', label: 'Transportation', description: 'Bus passes, ride services' },
    { icon: '💼', label: 'Employment', description: 'Job training, career help' },
    { icon: '🧠', label: 'Mental Health', description: 'Counseling, crisis support' },
  ];

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-lg">Find Help</h1>
            <p className="text-xs text-muted-foreground">Community resources near you</p>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass-card p-5 border-l-4 border-l-primary">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm mb-1">About FindHelp</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  We&apos;ve partnered with FindHelp.org to connect you with free or reduced-cost 
                  programs in your area. Enter your ZIP code to find food banks, healthcare, 
                  housing assistance, and more.
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* ZIP Code Search */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-xl bg-primary/10">
                <MapPin size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="font-heading font-semibold">Find Resources</h2>
                <p className="text-xs text-muted-foreground">Enter your ZIP code to get started</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Enter ZIP code (e.g., 90210)"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className="pl-10 rounded-xl h-12"
                  data-testid="zip-input"
                />
              </div>

              <Button
                onClick={handleFindResources}
                className="w-full bg-primary hover:bg-primary/90 rounded-full h-12"
                data-testid="find-resources-btn"
              >
                <ExternalLink size={18} className="mr-2" />
                Search Resources on FindHelp.org
              </Button>
            </div>
          </Card>
        </motion.section>

        {/* Resource Categories */}
        <section>
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
            Types of Help Available
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {resourceCategories.map((cat, i) => (
              <motion.div
                key={cat.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.05 }}
              >
                <Card className="glass-card p-4">
                  <div className="text-2xl mb-2">
                    {typeof cat.icon === 'string' ? cat.icon : <cat.icon size={24} className="text-primary" />}
                  </div>
                  <p className="font-medium text-sm">{cat.label}</p>
                  <p className="text-xs text-muted-foreground">{cat.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Crisis Resources */}
        <section>
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
            Crisis Resources
          </h2>
          <Card className="glass-card p-4 space-y-3">
            <a 
              href="tel:988" 
              className="flex items-center justify-between p-3 rounded-xl bg-[#D54B48]/10 hover:bg-[#D54B48]/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-[#D54B48]" />
                <div>
                  <p className="font-medium text-sm">988 Suicide & Crisis Lifeline</p>
                  <p className="text-xs text-muted-foreground">24/7 free, confidential support</p>
                </div>
              </div>
              <span className="text-primary font-medium">Call</span>
            </a>
            <a 
              href="tel:211" 
              className="flex items-center justify-between p-3 rounded-xl bg-primary/10 hover:bg-primary/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-primary" />
                <div>
                  <p className="font-medium text-sm">211 Community Resources</p>
                  <p className="text-xs text-muted-foreground">Connect to local services</p>
                </div>
              </div>
              <span className="text-primary font-medium">Call</span>
            </a>
          </Card>
        </section>

        {/* Disclaimer */}
        <p className="text-xs text-muted-foreground text-center px-4">
          HealthLink360 is not affiliated with FindHelp.org. You will be redirected to an external 
          website to search for resources. HealthLink360 does not verify the availability of listed services.
        </p>
      </main>

      <BottomNav />
    </div>
  );
}
